import { createSelector } from 'reselect'
import {
  getPupilsArray,
  getPupils
} from '../../../stores/pupils/pupils.selectors'
import {
  getGroups,
  getGroupsArray
} from '../../../stores/groups/groups.selectors'
import orderBy from 'lodash/orderBy'

const getData = (state, selector) => {
  const data = state.PupilsComponent ? state.PupilsComponent : state
  return !data ? null : selector(data)
}

const getSelectedPupils = state => getData(state, data => data.selectedPupils)

const getSelectedGroupsCategory = state =>
  getData(state, data => data.selectedGroupsCategory)

const isPupilSelected = (state, id) => !!getSelectedPupils(state)[id]

const getSortBy = state => getData(state, data => data.sortBy)

const getSearchValue = state => getData(state, data => data.searchValue)

const getFilterDialogOpened = state =>
  getData(state, data => data.filterDialogOpened)

const getSelectedGroups = state => getData(state, data => data.selectedGroups)

const getSelectedGroupsTemp = state =>
  getData(state, data => data.selectedGroupsTemp)

const isGroupSelected = (state, id) => getSelectedGroupsTemp(state)[id] || false

const getVisiblePupilsLength = state =>
  getData(state, data => data.visiblePupils)

const isShowingSelected = state => getData(state, data => data.showSelected)

const getSelectedGroupsArray = createSelector(
  [getSelectedGroups, getGroups],
  (selectedGroups, groups) => {
    if (
      !groups ||
      Object.keys(selectedGroups).length > Object.keys(groups).length
    )
      return []
    return Object.keys(selectedGroups)
      .filter(id => selectedGroups[id])
      .map(id => groups[id])
  }
)

const getSelectedPupilsIdsArray = createSelector(
  [getSelectedPupils],
  selectedPupils => {
    let selected = []
    Object.keys(selectedPupils).forEach(
      id => selectedPupils[id] && selected.push(id)
    )
    return selected
  }
)

const getSelectedPupilsArray = createSelector(
  [getPupils, getSelectedPupilsIdsArray],
  (pupils, ids) => ids.map(id => pupils[id])
)

const getSelectedPupilsLength = createSelector(
  [getSelectedPupils],
  selectedPupils => Object.keys(selectedPupils).length
)

const getFilteredPeopleByGroups = createSelector(
  [getSelectedGroups, getPupilsArray],
  (selectedGroups, pupils) =>
    !Object.keys(selectedGroups).length
      ? pupils
      : pupils.filter(
          pupil => pupil.groups.filter(group => selectedGroups[group.id]).length
        )
)

const getFilteredPeopleBySearch = createSelector(
  [getSearchValue, getFilteredPeopleByGroups],
  (searchValue, pupils) =>
    searchValue.length < 2
      ? pupils
      : pupils.filter(
          pupil =>
            pupil.account.full_name
              .toLowerCase()
              .indexOf(searchValue.toLowerCase()) !== -1
        )
)

const getPupilsConditionally = createSelector(
  [getFilteredPeopleBySearch, isShowingSelected, getSelectedPupilsArray],
  (filteredPupils, isShowingSelected, selectedPupils) =>
    isShowingSelected ? selectedPupils : filteredPupils
)

const getSortedPupils = createSelector(
  [getSortBy, getPupilsConditionally],
  (sort, pupilsArray) => {
    if (!sort.key) return pupilsArray
    const propsMap = {
      name: pupil =>
        pupil.account.full_name && pupil.account.full_name.toLowerCase(),
      age: pupil => pupil.year_group && pupil.year_group.order,
      permission: pupil => pupil.policy.can_publish,
      displayName: pupil =>
        pupil.account.display_name && pupil.account.display_name.toLowerCase()
    }
    return orderBy(pupilsArray, propsMap[sort.key], sort.direction)
  }
)

const isEveryoneSelected = createSelector(
  [getSelectedPupilsLength, getSortedPupils],
  (selectedPupilsLength, pupils) =>
    selectedPupilsLength === pupils.length ? true : false
)

const getPupilsWithAllFilters = createSelector(
  [getVisiblePupilsLength, getSortedPupils],
  (visiblePupilsLength, sortedPupils) =>
    sortedPupils.slice(0, visiblePupilsLength)
)

const isThereHiddenPupils = createSelector(
  [getVisiblePupilsLength, getSortedPupils],
  (visiblePupilsLength, sortedPupils) =>
    visiblePupilsLength < sortedPupils.length
)

const getFilteredGroups = createSelector(
  [getSelectedGroupsCategory, getGroupsArray],
  (selectedCategory, groups) =>
    selectedCategory === 'ALL'
      ? groups
      : groups.filter(group => group.category === selectedCategory)
)

export {
  isShowingSelected,
  getSelectedPupils,
  isPupilSelected,
  getSelectedPupilsLength,
  isEveryoneSelected,
  getSortedPupils,
  getPupilsWithAllFilters,
  getSortBy,
  getSearchValue,
  getFilteredPeopleBySearch,
  getFilterDialogOpened,
  getSelectedGroups,
  getSelectedGroupsTemp,
  getSelectedGroupsArray,
  isThereHiddenPupils,
  getSelectedPupilsIdsArray,
  getSelectedPupilsArray,
  isGroupSelected,
  getSelectedGroupsCategory,
  getFilteredGroups
}
